import React from "react";
import { portfolio } from "@/data/portfolio";
import useReveal from "@/hooks/useReveal";

const Role = ({ role, idx }) => {
  const ref = useReveal();
  return (
    <article
      data-testid={`experience-role-${idx}`}
      className="hairline-b grid grid-cols-1 md:grid-cols-12 gap-x-6 md:gap-x-10"
      style={{ borderColor: "var(--rule)" }}
    >
      {/* Sticky Left Column: roman numeral */}
      <div className="md:col-span-1 pt-12 md:pt-16 pb-16 md:pb-24">
        <div className="md:sticky md:top-24 romnum text-3xl md:text-4xl" style={{ color: "var(--accent)" }}>
          {role.roman}
        </div>
      </div>

      {/* Content Column */}
      <div ref={ref} className="reveal-up md:col-span-11 pt-12 md:pt-16 pb-16 md:pb-24">
        {/* Header Details */}
        <div className="grid grid-cols-1 md:grid-cols-11 gap-6 md:gap-10">
          <div className="md:col-span-3 font-mono text-[0.72rem] uppercase tracking-microcap" style={{ color: "var(--ink-muted)" }}>
            <div>{role.start}</div>
            <div className="opacity-60">— {role.end}</div>
            <div className="mt-4" style={{ color: "var(--ink)" }}>{role.kicker}</div>
          </div>
          <div className="md:col-span-8">
            <h3 className="font-serif text-3xl md:text-5xl tracking-tightest leading-[1.02]">
              {role.title}
            </h3>
            <div className="mt-2 font-mono text-[0.78rem] uppercase tracking-microcap" style={{ color: "var(--ink-muted)" }}>
              {role.company}
            </div>
            <p className="mt-6 font-serif text-lg md:text-xl leading-relaxed max-w-[64ch]">
              {role.summary}
            </p>
          </div>
        </div>

        {/* Bullets */}
        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-12">
          {role.bullets.map((b, i) => (
            <div key={i} data-testid={`experience-${idx}-bullet-${i}`} className="hairline-b pb-8" style={{ borderColor: "var(--rule)" }}>
              <div className="flex items-baseline justify-between mb-3">
                <h4 className="font-serif text-2xl md:text-3xl tracking-tightest">{b.title}</h4>
                {b.stat && (
                  <div className="text-right">
                    <div className="font-serif text-2xl md:text-3xl tabular" style={{ color: "var(--accent)" }}>{b.stat}</div>
                    <div className="font-mono text-[0.65rem] uppercase tracking-microcap" style={{ color: "var(--ink-muted)" }}>{b.statLabel}</div>
                  </div>
                )}
              </div>
              <p className="font-serif text-base md:text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>
                {b.lede}
              </p>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--ink-muted)" }}>
                {b.tail}
              </p>
            </div>
          ))}
        </div>
      </div>
    </article>
  );
};

const Experience = () => {
  const refHead = useReveal();
  const refStrip = useReveal();
  const { experience, byTheWork } = portfolio;
  return (
    <section
      id="experience"
      data-testid="experience-section"
      className="px-5 md:px-10 py-14 md:py-28"
    >
      <div className="mx-auto max-w-[1400px]">
        <div className="flex items-baseline justify-between mb-6 md:mb-10">
          <div className="section-tag">
            <span style={{ color: "var(--ink)" }}>{experience.section}</span>
            <span className="ml-3">{experience.eyebrow}</span>
          </div>
          <div className="section-tag">→ Roles I — II</div>
        </div>

        <h2 ref={refHead} className="reveal-up display text-4xl sm:text-5xl md:text-7xl lg:text-8xl max-w-[20ch] mb-10 md:mb-20">
          Five years shipping for{" "}
          <span className="display-italic" style={{ color: "var(--accent)" }}>international clients.</span>
        </h2>

        {/* By the work — large numbers strip */}
        <div ref={refStrip} className="reveal-up grid grid-cols-2 md:grid-cols-6 gap-5 md:gap-4 mb-14 md:mb-24 hairline-b pb-10 md:pb-12" style={{ borderColor: "var(--rule)" }}>
          {byTheWork.map((b, i) => (
            <div key={i} data-testid={`by-the-work-${i}`}>
              <div className="font-serif text-3xl sm:text-4xl md:text-5xl leading-none tabular tracking-tightest">{b.value}</div>
              <div className="font-mono text-[0.6rem] sm:text-[0.65rem] uppercase tracking-microcap mt-2" style={{ color: "var(--ink-muted)" }}>{b.unit}</div>
              <div className="font-serif text-xs sm:text-sm leading-tight mt-1 max-w-[18ch]">{b.label}</div>
            </div>
          ))}
        </div>

        {/* Roles */}
        <div>
          {experience.roles.map((r, i) => (
            <Role key={i} role={r} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
